import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link, useParams } from 'react-router-dom';
import { SeoAudit, AuditStatus } from '@/types/api/seo';
import * as Styles from './SeoAuditList.styles';

type Props = {
  audits: SeoAudit[];
  isLoading: boolean;
  onNewAudit: () => void;
};

const statusColor: Record<AuditStatus, 'default' | 'info' | 'success' | 'error'> = {
  [AuditStatus.QUEUED]: 'default',
  [AuditStatus.RUNNING]: 'info',
  [AuditStatus.COMPLETE]: 'success',
  [AuditStatus.FAILED]: 'error',
};

const SeoAuditListComponent = function ({ audits, isLoading, onNewAudit }: Props): JSX.Element {
  const { projectId = '' } = useParams();
  const basePath = `/seox/projects/${projectId}`;
  return (
    <Box sx={{}}>
      <Styles.PageHeader>
        <Styles.Title>Site Audits</Styles.Title>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewAudit}
          sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' } }}
        >
          New Audit
        </Button>
      </Styles.PageHeader>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#054AD9' }} />
        </Box>
      ) : audits.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary">
            No audits yet. Run your first site audit to get started.
          </Typography>
        </Box>
      ) : (
        <Styles.TableWrapper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Health Score</TableCell>
                  <TableCell>Pages Crawled</TableCell>
                  <TableCell>Issues Found</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {audits.map((audit) => (
                  <TableRow
                    key={audit.id}
                    component={Link}
                    to={`${basePath}/audits/${audit.shortId}`}
                    sx={{
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <TableCell>
                      {new Date(audit.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {audit.healthScore !== null ? (
                        <Typography
                          fontWeight={600}
                          color={
                            audit.healthScore >= 80
                              ? 'success.main'
                              : audit.healthScore >= 50
                                ? 'warning.main'
                                : 'error.main'
                          }
                        >
                          {audit.healthScore}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{audit.pagesCrawled}</TableCell>
                    <TableCell>{audit.issuesFound}</TableCell>
                    <TableCell>
                      <Chip
                        label={audit.status}
                        color={statusColor[audit.status]}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Styles.TableWrapper>
      )}
    </Box>
  );
};

export default SeoAuditListComponent;
